// src/controls/-utils.ts
function isItemOf(item, control) {
  return control.items.includes(item);
}
function asItemOf(item, control) {
  if (isItemOf(item, control)) {
    return item;
  }
  return void 0;
}
function isItemEnabled(item) {
  return !item.hasAttribute("aria-disabled") || item.getAttribute("aria-disabled") === "false";
}

// src/update-strategies/dom-observer-update-strategy.ts
var DomObserverUpdateStrategy = class {
  constructor(control) {
    this.control = control;
    this.observe();
  }
  observer = new MutationObserver((changes) => {
    if (!this.control) {
      return;
    }
    const changedItems = changes.some((change) => change.type === "childList");
    const itemAttributes = ["aria-disabled"];
    const changedItemAttributes = changes.some(
      (change) => change.type === "attributes" && isItemOf(change.target, this.control) && itemAttributes.includes(change.attributeName)
    );
    if (changedItems || changedItemAttributes) {
      this.control.readItems();
    }
    const optionAttributes = [...this.control.optionAttributes, "aria-disabled"];
    const changedOptions = changes.some(
      (change) => change.target === this.control.element && change.type === "attributes" && optionAttributes.includes(change.attributeName)
    );
    if (changedOptions) {
      this.control.readOptions();
    }
    const changedSelection = changes.every(
      (c) => c.type === "attributes" && c.attributeName === "aria-selected"
    );
    if (changedSelection) {
      this.control.readSelection();
    }
  });
  observe() {
    if (!this.control) {
      return;
    }
    this.observer.observe(this.control.element, {
      subtree: true,
      childList: true,
      attributes: true
    });
  }
  setControl(control) {
    this.observer.disconnect();
    this.control = control;
    this.observe();
  }
  dispose() {
    this.control = void 0;
    this.observer.disconnect();
  }
};

// src/controls/control.ts
function pipe(input, ...fns) {
  let lastResult = input;
  for (let fn of fns) {
    lastResult = fn(lastResult);
  }
  return lastResult;
}
var Control = class {
  items = [];
  get enabledItems() {
    if (this.options.disabled) {
      return [];
    }
    return this.items.filter(isItemEnabled);
  }
  element;
  emitter;
  updater;
  /**
   * Capabilities define, which _behaviors_ are applicable to the given control
   */
  #capabilities = {
    singleSelection: false,
    multiSelection: false
  };
  get capabilities() {
    return this.#capabilities;
  }
  #optionAttributes = [];
  get optionAttributes() {
    return this.#optionAttributes;
  }
  /**
   * Options instruct, which behaviors are actually _active_
   */
  options = {
    multiple: false,
    disabled: false,
    orientation: "horizontal"
  };
  navigationPatterns = [];
  constructor(element, options) {
    this.element = element;
    this.#capabilities = options.capabilities ?? this.#capabilities;
    this.#optionAttributes = options.optionAttributes ?? this.#optionAttributes;
    this.updater = options.updater ?? new DomObserverUpdateStrategy(this);
    if (options.updater) {
      options.updater.setControl(this);
    }
  }
  setEmitStrategy(emitter) {
    this.emitter?.dispose?.();
    this.emitter = emitter;
  }
  setUpdateStrategy(updater) {
    this.updater.dispose?.();
    this.updater = updater;
  }
  registerNavigationPatterns(patterns) {
    this.navigationPatterns = patterns;
    const eventNames = new Set(this.navigationPatterns.map((p) => p.eventListeners ?? []).flat());
    for (const eventName of eventNames) {
      this.element.addEventListener(eventName, this.handleEvent.bind(this));
    }
  }
  dispose() {
    this.updater.dispose?.();
    this.emitter?.dispose?.();
    this.focusStrategy.dispose();
    const eventNames = new Set(this.navigationPatterns.map((p) => p.eventListeners ?? []).flat());
    for (const eventName of eventNames) {
      this.element.removeEventListener(eventName, this.handleEvent.bind(this));
    }
  }
  handleEvent(event) {
    if (this.options.disabled) {
      return;
    }
    const patterns = this.navigationPatterns.filter((p) => p.matches(event));
    patterns.forEach((p) => p.prepare?.(event));
    pipe({ event }, ...patterns.map((p) => p.handle.bind(p)));
    event.stopPropagation();
  }
  // read in from DOM
  readOptions() {
    this.options.multiple = this.element.hasAttribute("aria-multiselectable") && this.element.getAttribute("aria-multiselectable") === "true" || false;
    this.options.disabled = this.element.hasAttribute("aria-disabled") && this.element.getAttribute("aria-disabled") === "true" || false;
    this.options.orientation = this.element.hasAttribute("aria-orientation") ? this.element.getAttribute("aria-orientation") : "horizontal";
  }
  readItems() {
    this.items = [];
  }
  readSelection() {
  }
};

// src/utils.ts
function uniqueId() {
  return ("30000000-1000-4000-2000" + -1e11).replace(
    /[0-3]/g,
    (a) => (a * 4 ^ Math.random() * 16 >> (a & 2)).toString(16)
  );
}

// src/navigation-patterns/focus-strategy.ts
var AbstractFocusStrategy = class {
  constructor(control, selectionStrategy) {
    this.control = control;
    this.selectionStrategy = selectionStrategy;
    this.selectionStrategy?.addListener("read", this.readSelectionHandler.bind(this));
  }
  eventListeners = ["focus", "focusin"];
  activeItem;
  prevActiveItem;
  get selection() {
    if (this.selectionStrategy) {
      return this.selectionStrategy.selection;
    }
    return [];
  }
  dispose() {
    this.selectionStrategy?.removeListener("read", this.readSelectionHandler.bind(this));
  }
  readSelectionHandler() {
    if (!this.hasFocus() && this.selection.length > 0) {
      this.activateSelection();
    }
  }
  hasFocus() {
    return this.control.element.contains(document.activeElement) || this.control.element === document.activeElement;
  }
  matches() {
    return this.control.enabledItems.length > 0;
  }
  handle(bag) {
    const { event, item } = bag;
    if (event.type === "focusin") {
      this.handleFocus(event);
      return bag;
    }
    if (item) {
      this.activateItem(item, event.type === "pointerover");
    }
    return bag;
  }
  handleFocus(event) {
    if (this.control.element === event.target) {
      const selectionPresent = this.selection.length > 0;
      if (selectionPresent) {
        this.activateSelection();
      } else {
        this.activateItem(this.control.enabledItems[0]);
      }
    } else if (this.control.enabledItems.includes(event.target)) {
      this.activateItem(event.target);
    }
  }
  activateSelection() {
    this.activateItem(this.selection[0]);
  }
};

// src/navigation-patterns/active-descendent-strategy.ts
var ActiveDescendentStrategy = class extends AbstractFocusStrategy {
  activateItem(item) {
    if (item === this.activeItem) {
      return;
    }
    item.setAttribute("aria-current", "true");
    this.control.element.setAttribute("aria-activedescendant", item.id);
    this.prevActiveItem = this.activeItem;
    this.activeItem = item;
    this.control.prevActiveItem?.removeAttribute("aria-current");
    this.control.emitter?.itemActivated(item);
  }
  updateItems() {
    for (const item of this.control.items) {
      if (!item.id) {
        item.id = uniqueId();
      }
    }
  }
};

// src/navigation-patterns/end-navigation.ts
function isEndEvent(event) {
  return event instanceof KeyboardEvent && event.type === "keydown" && event.key === "End";
}
var EndNavigation = class {
  constructor(control) {
    this.control = control;
  }
  eventListeners = ["keydown"];
  matches(event) {
    return isEndEvent(event) && this.control.enabledItems.length > 0;
  }
  handle(bag) {
    const lastOffset = this.control.enabledItems.length - 1;
    return {
      ...bag,
      item: this.control.enabledItems[lastOffset]
    };
  }
};

// src/navigation-patterns/home-navigation.ts
function isHomeEvent(event) {
  return event instanceof KeyboardEvent && event.type === "keydown" && event.key === "Home";
}
var HomeNavigation = class {
  constructor(control) {
    this.control = control;
  }
  eventListeners = ["keydown"];
  matches(event) {
    return isHomeEvent(event) && this.control.enabledItems.length > 0;
  }
  handle(bag) {
    return {
      ...bag,
      item: this.control.enabledItems[0]
    };
  }
};

// src/navigation-patterns/next-navigation.ts
function matchesKeys(event, keyOrKeys) {
  const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];
  return event instanceof KeyboardEvent && event.type === "keydown" && keys.includes(event.key);
}
var NextNavigation = class {
  constructor(control, keyOrKeys) {
    this.control = control;
    this.keyOrKeys = keyOrKeys;
  }
  eventListeners = ["keydown"];
  matches(event) {
    return matchesKeys(event, this.keyOrKeys) && this.control.enabledItems.length > 0;
  }
  handle(bag) {
    let item = void 0;
    if (this.control.activeItem) {
      const activeIndex = this.control.enabledItems.indexOf(this.control.activeItem);
      if (activeIndex !== this.control.enabledItems.length - 1) {
        item = this.control.enabledItems[activeIndex + 1];
      }
    }
    return {
      ...bag,
      item
    };
  }
};

// src/navigation-patterns/pointer-navigation.ts
var PointerNavigation = class {
  constructor(control, typeOrTypes = "pointerup") {
    this.control = control;
    this.eventListeners = Array.isArray(typeOrTypes) ? typeOrTypes : [typeOrTypes];
  }
  eventListeners;
  matches(event) {
    return this.eventListeners.includes(event.type);
  }
  handle(bag) {
    const { event } = bag;
    const item = event.composedPath().find((elem) => asItemOf(elem, this.control));
    return {
      ...bag,
      item
    };
  }
};

// src/navigation-patterns/previous-navigation.ts
function matchesKeys2(event, keyOrKeys) {
  const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];
  return event instanceof KeyboardEvent && event.type === "keydown" && keys.includes(event.key);
}
var PreviousNavigation = class {
  constructor(control, keyOrKeys) {
    this.control = control;
    this.keyOrKeys = keyOrKeys;
  }
  eventListeners = ["keydown"];
  matches(event) {
    return matchesKeys2(event, this.keyOrKeys) && this.control.enabledItems.length > 0;
  }
  handle(bag) {
    let item = void 0;
    if (this.control.activeItem) {
      const activeIndex = this.control.enabledItems.indexOf(this.control.activeItem);
      if (activeIndex !== 0) {
        item = this.control.enabledItems[activeIndex - 1];
      }
    }
    return {
      ...bag,
      item
    };
  }
};

// src/navigation-patterns/scroll-to-item.ts
var ScrollToItem = class {
  constructor(control) {
    this.control = control;
  }
  eventListeners = ["keydown"];
  matches() {
    return this.control.items.length > 0;
  }
  handle(bag) {
    const { event, item } = bag;
    if (event.type === "keydown") {
      if (item) {
        this.scrollToItem(item);
      }
      this.preventScrolling(event);
    }
    return bag;
  }
  preventScrolling(event) {
    if (event.key === "ArrowUp" || event.key === "ArrowDown" || event.key === "ArrowLeft" || event.key === "ArrowRight" || event.key === "PageUp" || event.key === "PageDown" || event.key === "Home" || event.key === "End" || event.key === " " || event.key === "a" && event.metaKey) {
      event.preventDefault();
    }
  }
  scrollToItem(item) {
    if (this.control.prevActiveItem && this.control.items.indexOf(this.control.prevActiveItem) < this.control.items.indexOf(item)) {
      this.scrollDownwardsToItem(item);
    } else {
      this.scrollUpwardsToItem(item);
    }
  }
  scrollUpwardsToItem(item) {
    if (!this.isItemInViewport(item) || item.offsetTop === 0) {
      const buffer = this.calcBuffer();
      this.control.element.scrollTop = item.offsetTop - buffer;
    }
  }
  scrollDownwardsToItem(item) {
    if (!this.isItemInViewport(item)) {
      const buffer = this.calcBuffer();
      this.control.element.scrollTop = item.offsetTop - this.control.element.clientHeight + item.clientHeight + buffer;
    }
  }
  isItemInViewport(item) {
    const buffer = this.calcBuffer();
    const viewportLowerEdge = this.control.element.scrollTop + this.control.element.clientHeight;
    const itemLowerEdge = item.offsetTop + item.clientHeight + buffer;
    const visibleFromTop = viewportLowerEdge >= itemLowerEdge;
    const viewportUpperEdge = this.control.element.scrollTop;
    const itemUpperEdge = item.offsetTop - buffer;
    const visibleFromBottom = viewportUpperEdge <= itemUpperEdge;
    const itemIsTallerThanContainer = item.clientHeight > this.control.element.clientHeight;
    return visibleFromTop && visibleFromBottom && !itemIsTallerThanContainer;
  }
  calcBuffer() {
    const style = window.getComputedStyle(this.control.element);
    const padding = style.getPropertyValue("padding-block");
    return Math.max(2, Number.parseFloat(padding));
  }
};

// src/navigation-patterns/selection-strategy.ts
import isEqual from "lodash.isequal";
var DEFAULT_BEHAVIOR = {
  singleSelection: "automatic"
};
var SelectionStrategy = class {
  constructor(control, behavior) {
    this.control = control;
    this.behavior = {
      ...DEFAULT_BEHAVIOR,
      ...behavior ?? {}
    };
    this.readSelection();
  }
  #listeners = {
    read: /* @__PURE__ */ new Set()
  };
  eventListeners = ["focusin", "keydown", "keyup", "pointerup", "change"];
  #selection = [];
  get selection() {
    return this.#selection;
  }
  shiftItem;
  behavior;
  dispose() {
    Object.values(this.#listeners).forEach((listeners) => listeners.clear());
  }
  addListener(event, handler) {
    this.#listeners[event].add(handler);
  }
  removeListener(event, handler) {
    this.#listeners[event].delete(handler);
  }
  matches(event) {
    return this.control.items.length > 0 && this.eventListeners.includes(event.type);
  }
  prepare(event) {
    if (!this.shiftItem && this.control.activeItem && event instanceof KeyboardEvent && event.shiftKey) {
      this.shiftItem = this.control.activeItem;
    }
  }
  handle(bag) {
    const { event } = bag;
    if (event.type === "focusin") {
      this.handleFocus();
    } else if (event.type === "pointerup" && bag.item) {
      this.handlePointer(event, bag.item);
    } else if (event instanceof KeyboardEvent) {
      this.handleKeyboard(event, bag.item);
    } else if (event.type === "change") {
      this.handleChange();
    }
    return bag;
  }
  select(selection) {
    const items = selection.every(Number) ? selection.map((sel) => this.control.items.find((item) => item === sel)).filter((e) => e !== void 0) : selection;
    this.persistSelection(items);
  }
  readSelection() {
    this.#selection = [
      ...this.control.element.querySelectorAll('[aria-selected="true"]')
    ];
    for (const listener of this.#listeners.read) {
      listener();
    }
  }
  handleChange() {
    this.readSelection();
    this.control.emitter?.selected(this.#selection);
  }
  handleFocus() {
    const multiple = this.control.options.multiple;
    const selectionPresent = this.#selection.length > 0;
    if (this.control.capabilities.singleSelection && !multiple && !selectionPresent) {
      this.selectSingle(this.control.items[0]);
    }
  }
  handlePointer(event, item) {
    if (event.shiftKey) {
      this.selectShift(item);
    } else if (event.metaKey) {
      if (this.#selection.includes(item)) {
        this.deselect(item);
      } else {
        this.selectAdd(item);
      }
    } else {
      this.selectSingle(item);
    }
  }
  handleKeyboard(event, item) {
    if (event.type === "keydown") {
      if (item) {
        this.handleItem(event, item);
      }
      this.handleKeys(event);
      this.handleKeyCombinations(event);
    } else if (event.type === "keyup" && !event.shiftKey) {
      this.shiftItem = void 0;
    }
  }
  handleItem(event, item) {
    if (this.control.options.multiple) {
      if (event.shiftKey) {
        this.selectShift(item);
      }
    } else {
      if (this.behavior.singleSelection === "automatic" || // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      this.behavior.singleSelection === "manual" && event.key === " ") {
        this.selectSingle(item);
      }
    }
  }
  handleKeys(event) {
    if (this.behavior.singleSelection === "manual" && event.key === " " && this.control.activeItem) {
      this.selectSingle(this.control.activeItem);
    }
  }
  /**
   * Handles special keyboard control cases, such as handling the spacebar key
   * and cmd/ctrl + a
   */
  handleKeyCombinations(event) {
    if (event.key === " " && this.control.activeItem && this.control.options.multiple) {
      if (this.#selection.includes(this.control.activeItem)) {
        this.deselect(this.control.activeItem);
      } else {
        this.selectAdd(this.control.activeItem);
      }
    } else if ((event.key === "KeyA" || event.key === "a") && event.metaKey) {
      this.selectAll();
      event.preventDefault();
      event.stopPropagation();
    }
  }
  // selection logic
  deselect(item) {
    if (this.#selection.includes(item)) {
      const selection = this.#selection.slice();
      selection.splice(selection.indexOf(item), 1);
      this.persistSelection(selection);
    }
  }
  selectSingle(item) {
    this.shiftItem = item;
    this.persistSelection([item]);
  }
  selectAdd(item) {
    const selection = this.control.options.multiple ? this.#selection.slice() : [];
    selection.push(item);
    this.shiftItem = item;
    this.persistSelection(selection);
  }
  selectAll() {
    if (this.control.options.multiple) {
      this.persistSelection(this.control.items);
    }
  }
  selectRange(from, to) {
    if (this.control.options.multiple) {
      const selection = [];
      let i = from;
      const up = to > from;
      while (up ? i <= to : i >= to) {
        selection.push(this.control.items[i]);
        i += up ? 1 : -1;
      }
      this.persistSelection(selection);
    }
  }
  selectShift(item) {
    if (this.control.options.multiple && this.shiftItem) {
      const indexShift = this.control.items.indexOf(this.shiftItem);
      const indexItem = this.control.items.indexOf(item);
      this.selectRange(indexShift, indexItem);
    }
  }
  // talk to the control from here
  persistSelection(selection) {
    if (isEqual(selection, this.#selection)) {
      return;
    }
    for (const element of this.control.items) {
      if (selection.includes(element)) {
        element.setAttribute("aria-selected", "true");
      } else {
        element.removeAttribute("aria-selected");
      }
    }
    this.#selection = selection;
    this.control.emitter?.selected(this.#selection);
  }
};

// src/controls/listbox.ts
var Listbox = class extends Control {
  #selectionStrategy = new SelectionStrategy(this);
  focusStrategy = new ActiveDescendentStrategy(
    this,
    this.#selectionStrategy
  );
  get selection() {
    return this.#selectionStrategy.selection;
  }
  get activeItem() {
    return this.focusStrategy.activeItem;
  }
  get prevActiveItem() {
    return this.focusStrategy.prevActiveItem;
  }
  constructor(element, options) {
    super(element, {
      capabilities: {
        singleSelection: true,
        multiSelection: true
      },
      optionAttributes: ["aria-multiselectable"],
      ...options
    });
    this.registerNavigationPatterns([
      new NextNavigation(this, ["ArrowDown", "ArrowRight"]),
      new PreviousNavigation(this, ["ArrowUp", "ArrowLeft"]),
      new HomeNavigation(this),
      new EndNavigation(this),
      new PointerNavigation(this),
      this.focusStrategy,
      new ScrollToItem(this),
      this.#selectionStrategy
    ]);
    element.role = "listbox";
    if (!element.hasAttribute("tabindex")) {
      element.setAttribute("tabindex", "0");
    }
    this.readOptions();
    this.readItems();
  }
  dispose() {
    super.dispose();
    this.#selectionStrategy.dispose();
  }
  readItems() {
    this.items = [...this.element.querySelectorAll('[role="option"]')];
    this.#selectionStrategy.select(
      this.selection.filter((selection) => this.items.includes(selection))
    );
    this.focusStrategy.updateItems();
  }
  readSelection() {
    this.#selectionStrategy.readSelection();
  }
  readOptions() {
    super.readOptions();
    this.element.setAttribute("tabindex", this.options.disabled ? "-1" : "0");
    this.focusStrategy.updateItems();
  }
};

// src/navigation-patterns/menu-navigation.ts
var OPENER = Symbol("Opener");
var FOCUS_ON_OPEN = Symbol("FocusOnOpen");
var FOCUS_TRIGGER_ON_CLOSE = Symbol("FocusTriggerOnClose");
function isToggleEvent(event) {
  return event.type === "toggle";
}
function isPointerEvent(event) {
  return ["pointerover", "pointerout", "pointerup"].includes(event.type);
}
function getMenuFromItem(item) {
  return document.getElementById(
    item.getAttribute("popovertarget")
  );
}
var MenuNavigation = class {
  constructor(control, focusStrategy) {
    this.control = control;
    this.focusStrategy = focusStrategy;
  }
  eventListeners = ["keydown", "toggle", "pointerover", "pointerout", "pointerup"];
  matches(event) {
    return event instanceof KeyboardEvent && (event.key === " " || event.key === "Enter" || event.key === "ArrowRight" || event.key === "ArrowLeft") || event.type === "toggle" || event.type === "pointerover" || event.type === "pointerout" || event.type === "pointerup";
  }
  handle(bag) {
    const { event } = bag;
    if (event.type === "keydown") {
      this.navigateWithKeyboard(event);
    } else if (isPointerEvent(event)) {
      this.navigateWithPointer(event);
    } else if (isToggleEvent(event)) {
      if (event.newState === "open") {
        this.show();
      } else {
        this.hide();
      }
    }
    return bag;
  }
  navigateWithKeyboard(event) {
    if (event.key === "ArrowRight" && this.control.activeItem?.hasAttribute("popovertarget")) {
      this.showSubmenu(true);
    }
    if (event.key === "ArrowLeft") {
      this.hideSubmenu();
    }
    if (!this.control.activeItem?.hasAttribute("popovertarget") && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      this.control.activeItem?.click();
      this.closeRootMenu();
    }
  }
  navigateWithPointer(event) {
    const target = event.target;
    if (event.type === "pointerover") {
      this.control.items.filter((item) => item !== this.control.activeItem).filter((item) => item.hasAttribute("popovertarget")).forEach((item) => {
        const menu = getMenuFromItem(item);
        if (menu) {
          menu[FOCUS_TRIGGER_ON_CLOSE] = false;
          menu.hidePopover();
        }
      });
      if (this.control.activeItem?.hasAttribute("popovertarget")) {
        this.showSubmenu();
      }
    } else if (event.type === "pointerout") {
      if (target === this.control.element && event.relatedTarget === this.control.element[OPENER]) {
        event.relatedTarget.focus();
      }
    } else if (event.type === "pointerup") {
      if (this.control.items.find((item) => item.contains(target)) && !this.control.activeItem?.hasAttribute("popovertarget")) {
        window.setTimeout(() => this.closeRootMenu(), 0);
      }
    }
  }
  showSubmenu(moveFocus = false) {
    if (this.control.activeItem) {
      const menu = getMenuFromItem(this.control.activeItem);
      if (menu) {
        menu[OPENER] = this.control.activeItem;
        menu[FOCUS_ON_OPEN] = moveFocus;
        menu.showPopover();
      }
    }
  }
  hideSubmenu() {
    this.control.element.hidePopover();
  }
  show() {
    for (const item of this.control.items) {
      item.setAttribute("tabindex", "-1");
    }
    if (this.control.enabledItems.length > 0) {
      this.control.enabledItems[0].setAttribute("tabindex", "0");
    }
    if (this.control.element[FOCUS_ON_OPEN] !== false) {
      if (this.control.enabledItems.length > 0) {
        this.control.enabledItems[0].focus();
      }
    }
  }
  hide() {
    this.focusStrategy.activeItem = void 0;
    const focusTriggerOnClose = this.control.element[FOCUS_TRIGGER_ON_CLOSE];
    if (focusTriggerOnClose !== false) {
      const trigger = this.control.element[OPENER];
      if (trigger) {
        trigger.focus();
      }
    }
  }
  closeRootMenu() {
    const getRootMenu = (menu) => {
      const menus = [];
      let elem = menu;
      while (elem) {
        if (elem.getAttribute("role") === "menu" && elem.hasAttribute("popover")) {
          menus.push(elem);
        }
        elem = elem.parentElement;
      }
      return menus.pop();
    };
    const root = getRootMenu(this.control.element);
    if (root) {
      root.hidePopover();
    }
  }
};

// src/navigation-patterns/roving-tabindex-strategy.ts
var RovingTabindexStrategy = class extends AbstractFocusStrategy {
  activateItem(item, forceFocus = false) {
    if (this.control.options.disabled) {
      return;
    }
    if (item !== this.activeItem) {
      item.setAttribute("tabindex", "0");
      if (this.activeItem) {
        this.prevActiveItem = this.activeItem;
        this.prevActiveItem.setAttribute("tabindex", "-1");
      }
    }
    if (this.hasFocus() || forceFocus) {
      item.focus();
    }
    if (item !== this.activeItem) {
      this.activeItem = item;
      this.control.emitter?.itemActivated(item);
    }
  }
  updateItems() {
    for (const item of this.control.items) {
      item.setAttribute("tabindex", "-1");
    }
    if (this.control.options.disabled) {
      this.activeItem?.setAttribute("tabindex", "-1");
      this.activeItem = void 0;
    } else {
      if (this.activeItem && isItemEnabled(this.activeItem)) {
        this.activeItem.setAttribute("tabindex", "0");
      } else {
        this.activeItem = void 0;
      }
      if (!this.activeItem && this.control.enabledItems.length > 0) {
        this.control.enabledItems[0].setAttribute("tabindex", "0");
      }
    }
  }
};

// src/controls/menu.ts
var Menu = class extends Control {
  focusStrategy = new RovingTabindexStrategy(this);
  get selection() {
    return [];
  }
  get activeItem() {
    return this.focusStrategy.activeItem;
  }
  get prevActiveItem() {
    return this.focusStrategy.prevActiveItem;
  }
  constructor(element, options) {
    super(element, {
      capabilities: {
        singleSelection: false,
        multiSelection: false
      },
      ...options
    });
    this.registerNavigationPatterns([
      new NextNavigation(this, ["ArrowDown"]),
      new PreviousNavigation(this, ["ArrowUp"]),
      new HomeNavigation(this),
      new EndNavigation(this),
      new PointerNavigation(this, "pointerover"),
      this.focusStrategy,
      new MenuNavigation(this, this.focusStrategy),
      new ScrollToItem(this)
    ]);
    element.role = "menu";
    this.readOptions();
    this.readItems();
  }
  readItems() {
    const items = this.element.querySelectorAll(
      '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]'
    );
    this.items = Array.from(items).filter((item) => {
      const closestMenu = item.closest('menu,[role="menu"]');
      return !closestMenu || closestMenu === this.element;
    });
    this.focusStrategy.updateItems();
  }
  readOptions() {
    super.readOptions();
    this.focusStrategy.updateItems();
  }
};

// src/controls/tablist.ts
var Tablist = class extends Control {
  #selectionStrategy;
  focusStrategy;
  #nextNavigation = new NextNavigation(this, "ArrowRight");
  #prevNavigation = new PreviousNavigation(this, "ArrowLeft");
  get selection() {
    return this.#selectionStrategy.selection;
  }
  get activeItem() {
    return this.focusStrategy.activeItem;
  }
  get prevActiveItem() {
    return this.focusStrategy.prevActiveItem;
  }
  constructor(element, options) {
    super(element, {
      capabilities: {
        singleSelection: true,
        multiSelection: false
      },
      optionAttributes: ["aria-orientation"],
      ...options
    });
    this.#selectionStrategy = new SelectionStrategy(this, options?.behavior ?? {});
    this.focusStrategy = new RovingTabindexStrategy(this, this.#selectionStrategy);
    this.registerNavigationPatterns([
      this.#nextNavigation,
      this.#prevNavigation,
      new HomeNavigation(this),
      new EndNavigation(this),
      new PointerNavigation(this),
      this.focusStrategy,
      this.#selectionStrategy
    ]);
    element.role = "tablist";
    this.readOptions();
    this.readItems();
  }
  dispose() {
    super.dispose();
    this.#selectionStrategy.dispose();
  }
  readItems() {
    this.items = [...this.element.querySelectorAll('[role="tab"]')];
    this.#selectionStrategy.select(
      this.selection.filter((selection) => this.items.includes(selection))
    );
    this.ensureSelection();
    this.focusStrategy.updateItems();
  }
  readSelection() {
    this.#selectionStrategy.readSelection();
  }
  readOptions() {
    super.readOptions();
    this.#nextNavigation.keyOrKeys = this.options.orientation === "horizontal" ? "ArrowRight" : "ArrowDown";
    this.#prevNavigation.keyOrKeys = this.options.orientation === "horizontal" ? "ArrowLeft" : "ArrowUp";
    this.focusStrategy.updateItems();
  }
  ensureSelection() {
    if (this.selection.length === 0 && this.items.length > 0) {
      this.focusStrategy.activateItem(this.items[0]);
      this.#selectionStrategy.select([this.items[0]]);
    }
  }
};

// src/emit-strategies/index-emit-strategy.ts
var IndexEmitStrategy = class {
  constructor(control, options) {
    this.control = control;
    this.options = options;
    this.control = control;
    this.control.setEmitStrategy(this);
  }
  selected(selection) {
    const indices = selection.map((i) => this.control.items.indexOf(i)).filter((i) => i !== -1);
    return this.options.select?.(indices);
  }
  itemActivated(item) {
    const index = this.control.items.indexOf(item);
    return this.options.activateItem?.(index);
  }
  dispose() {
    this.control = void 0;
  }
};

// src/emit-strategies/item-emit-strategy.ts
var ItemEmitStrategy = class {
  constructor(control, options) {
    this.control = control;
    this.options = options;
    this.control = control;
    this.control.setEmitStrategy(this);
  }
  selected(selection) {
    return this.options.select?.(selection);
  }
  itemActivated(item) {
    return this.options.activateItem?.(item);
  }
  dispose() {
    this.control = void 0;
  }
};

// src/update-strategies/reactive-update-strategy.ts
var ReactiveUpdateStrategy = class {
  constructor(control) {
    this.control = control;
  }
  setControl(control) {
    this.control = control;
  }
  updateItems() {
    this.control?.readItems();
  }
  updateSelection() {
    this.control?.readSelection();
  }
  updateOptions() {
    this.control?.readOptions();
  }
  dispose() {
    this.control = void 0;
  }
};
export {
  Control,
  DomObserverUpdateStrategy,
  IndexEmitStrategy,
  ItemEmitStrategy,
  Listbox,
  Menu,
  ReactiveUpdateStrategy,
  Tablist
};
